import React, { useEffect, useMemo } from 'react';
import ActivityCard from '../components/layout/ActivityCard';
import { getActivityByID } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { History } from 'lucide-react';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';

const getActivityTimestamp = (item) => {
  const raw =
    item?.created_at ||
    item?.createdAt ||
    item?.date ||
    item?.updated_at ||
    item?.updatedAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const ActivityList = ({ salesId, variant = 'default' }) => {
  const userId = Cookies.get('userId');
  const { id } = useParams();

  const ID = salesId ? salesId : id;
  const {
    data: activities,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['activityById', parseInt(ID)],
    queryFn: async () => {
      const res = await getActivityByID(parseInt(ID));
      return res;
    },
    enabled: !!ID
  });

  useEffect(() => {
    if (!ID) return;
    mixpanel.track('Stage History Viewed', {
      user_id: userId,
      lead_id: ID,
      timestamp: new Date().toISOString()
    });
  }, [userId, ID]);

  const sorted = useMemo(() => {
    if (!Array.isArray(activities)) return [];
    return [...activities].sort((a, b) => getActivityTimestamp(b) - getActivityTimestamp(a));
  }, [activities]);

  const rootClassName =
    variant === 'split'
      ? 'mt-2 w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]'
      : 'mt-2 w-full min-w-0 max-w-full self-stretch overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04] sm:w-[70%] sm:self-center';

  return (
    <div className={rootClassName}>
      <div className="border-b border-slate-100/80 bg-gradient-to-r from-teal-50 via-white to-slate-50 px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-700/12 text-teal-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
            aria-hidden>
            <History className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900">Stage history</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Newest first — each step shows when the lead moved through your pipeline.
              </p>
            </div>
            {!isLoading && !isError && sorted.length > 0 && (
              <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm sm:self-auto">
                <img
                  src="/icons/clocks.svg"
                  alt=""
                  className="h-3.5 w-3.5 shrink-0 object-contain opacity-80"
                  width={14}
                  height={14}
                  aria-hidden
                />
                {sorted.length} stage{sorted.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
      {isLoading && (
        <div className="flex w-full flex-col gap-6 py-2" aria-busy="true" aria-label="Loading stage history">
          {[0, 1, 2].map((key) => (
            <div key={key} className="flex gap-3">
              <div className="flex w-11 flex-shrink-0 justify-center pt-7">
                <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
              </div>
              <div className="min-w-0 flex-1 space-y-2 pt-6">
                <div className="h-3 w-28 animate-pulse rounded bg-gray-200" />
                <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-red-100 bg-red-50/60 px-6 py-10 text-center">
          <p className="text-sm font-medium text-red-900">Could not load stage history</p>
          <p className="max-w-md text-xs text-red-800/90">
            {error?.response?.data?.message || error?.message || 'Something went wrong. Try again.'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-teal-800">
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && sorted.length > 0 && (
        <div className="relative w-full">
          {/* One spine for the whole list so gaps between cards (pb-10) do not break the line */}
          <div
            className="pointer-events-none absolute bottom-0 left-[22px] top-0 z-0 w-[3px] -translate-x-1/2 rounded-full bg-teal-500/85 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
            aria-hidden
          />
          <div className="relative z-[1] w-full">
            {sorted.map((item, index) => (
              <ActivityCard
                key={item?.id ?? `${getActivityTimestamp(item)}-${index}`}
                data={item}
                isLatest={index === 0}
                isLast={index === sorted.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {!isLoading && !isError && sorted.length === 0 && (
        <div className="flex justify-start py-10">
          <div className="flex max-w-md flex-col items-start justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-8 py-12 text-left">
            <img
              src={NoDataFoundImg}
              alt=""
              className="w-[45%] max-w-[200px] object-contain opacity-90"
            />
            <h3 className="text-lg font-semibold text-gray-800">No stages yet</h3>
            <p className="text-sm leading-relaxed text-gray-600">
              When this lead moves through the pipeline, each stage will appear here in order.
            </p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ActivityList;
